import * as React from "react";
import clsx from "classnames";
import lodashGet from "lodash/get";
import { useDispatch, useSelector } from "react-redux";

// material ui
import { makeStyles } from "@material-ui/styles";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Typography from "@material-ui/core/Typography";
import MuiButton from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";

import ErrorIcon from "@material-ui/icons/ErrorOutline";
import ActionCancel from "@material-ui/icons/Cancel";
import ActionSave from "@material-ui/icons/Save";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { showNotification, Button, SweetAlert } from "jazasoft";

// local
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";
import ExcelErrorDialog from "../../../components/ExcelErrorDialog";

import useDeepMemo from "../../../hooks/useDeepMemo";
import useSafeState from "../../../hooks/useSafeState";
import { getDistinctValues, getStyleColor, isEmpty, isFalse, isTrue, listFromCsv, toFixed } from "../../../utils/helpers";
import { findSizeBreakupList, getCutOrderFlowInfoList } from "../../../helpers/orderHelper";
import { MarkerType, OState, Role, TaskNames } from "../../../utils/types";

import useMutation from "../../../hooks/useMutation";
import { getColumnsMarkerDetails, getColumnsSizeBreakup, getCutOrderRows, getRowsMarkerDetails, getRowsSizeBreakup } from "../cutPlanHelper";
import { UPDATE_TASK } from "../../../reducers/scheduledTaskReducer";
import CutPlanAdjustDialog from "./dialog/CutPlanAdjustDialog";
import DialogNames from "./dialog/DialogNames";
import MarkerImportDialog from "./dialog/MarkerImportDialog";
import MarkerUploadDialog from "./dialog/MarkerUploadDialog";
import { CutPlanType, LotType, MiscType } from "../../../constants";
import ManualMarkerDialog from "./dialog/ManualMarkerDialog";
import useQuery from "../../../hooks/useQuery";
import FullScreen from "../../../components/FullScreen";
import Tooltip from "../../../components/Tooltip";
import useSetting from "../../../hooks/useSetting";
import SettingKeys from "../../../utils/SettingKeys";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { convertTableToCell } from "../../../utils/excelHelper";
import { useTenant } from "../../../hooks";
import SplicePointDrawer from "./dialog/SplicePointDrawer";

const isEqual = (a, b) => (!isEmpty(a) && !isEmpty(b) ? a === b : isEmpty(a) && isEmpty(b));

const normalizeMarkerText = value => (isEmpty(value) ? null : `${value}`.trim().toUpperCase());

const normalizeMarkerNumber = value => {
  if (isEmpty(value)) return null;
  const number = Number(value);
  return Number.isNaN(number) ? normalizeMarkerText(value) : number;
};

// Returning string null intentionally to detect an invalid pattern format.
const parseMarkerPattern = input => {
  if (isEmpty(input)) return { warpPattern: null, weftPattern: null };
  const parts = typeof input === "string" ? input.split(",") : [];
  if (parts.length !== 2) return { warpPattern: "null", weftPattern: "null" };

  const values = parts.map(part => {
    const match = part.trim().replace(/[WL%]/gi, "");
    return Number(match) ? Number(match) : "null";
  });
  return { warpPattern: values[0], weftPattern: values[1] };
};

const getEffectiveMarkerPattern = marker => {
  const group = marker?.group || {};
  const useGroupPattern = !isEmpty(group.warpPattern) && !isEmpty(group.weftPattern);

  return {
    patternNumber: !isEmpty(group.patternNumber) ? group.patternNumber : marker?.patternNumber,
    warpPattern: useGroupPattern ? group.warpPattern : marker?.warpPattern,
    weftPattern: useGroupPattern ? group.weftPattern : marker?.weftPattern
  };
};

const isSameMarkerIdentity = (first, second) => {
  const firstPattern = getEffectiveMarkerPattern(first);
  const secondPattern = getEffectiveMarkerPattern(second);

  return (
    normalizeMarkerText(first?.markerNo) === normalizeMarkerText(second?.markerNo) &&
    normalizeMarkerText(firstPattern.patternNumber) === normalizeMarkerText(secondPattern.patternNumber) &&
    normalizeMarkerNumber(firstPattern.warpPattern) === normalizeMarkerNumber(secondPattern.warpPattern) &&
    normalizeMarkerNumber(firstPattern.weftPattern) === normalizeMarkerNumber(secondPattern.weftPattern)
  );
};

const normalizeSizeValue = value => `${value ?? ""}`.trim().toUpperCase();

const findMatchingSizeBreakup = (sizeBreakupList, sizeKey, size, sizeGroup) =>
  sizeBreakupList.find(sb =>
    isEmpty(sizeGroup)
      ? normalizeSizeValue(sb.size) === normalizeSizeValue(size)
      : normalizeSizeValue(sb.sizeKey) === normalizeSizeValue(sizeKey)
  );

const requiredCols = ["markerNo", "plies", "width", "csv"];

const bgColors = ["38,174,135", "73,215,175"]; //							 RGB (73, 215, 175)

const getMarkerColumns = (order, cutOrder, enablePerimeterUpdate = false) => {
  let { columns, headerRows } = getColumnsMarkerDetails(order, cutOrder, enablePerimeterUpdate);

  if (cutOrder?.lotType === LotType.BULK) {
    columns = columns.filter(
      c =>
        ![
          "ply",
          "qty",
          "qtyPercentage",
          "fabric",
          "groupName",
          "patternValue",
          "patternNumber",
          "length",
          "markerLength",
          "layLength",
          "efficiency",
          "perimeter",
          "prodType",
          "remarks"
        ].includes(c.dataKey)
    );
    columns = [
      { dataKey: "id", title: "ID" },
      ...columns,
      { dataKey: "qtyPercent", title: "Quantity (%)" },
      { dataKey: "width", title: "Width (CM)" },
      { dataKey: "patternValue", title: "Pattern Value" },
      { dataKey: "patternNumber", title: "Pattern No" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "length", title: "Length" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "perimeter", title: "Perimeter" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  } else {
    columns = columns.filter(
      c =>
        ![
          "ply",
          "shade",
          "qty",
          "qtyPercentage",
          "width",
          "patternValue",
          "csv",
          "prodType",
          "remarks",
          "fabric",
          "balanceFabric",
          "groupName",
          "patternNumber",
          "markerLength",
          "layLength",
          "length",
          "efficiency",
          "perimeter"
        ].includes(c.dataKey)
    );
    columns = [
      { dataKey: "id", title: "ID" },
      ...columns,
      { dataKey: "qty", title: "Qty" },
      { dataKey: "length", title: "Length" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "perimeter", title: "Perimeter" },
      { dataKey: "fabric", title: "Fab. Required" },
      { dataKey: "width", title: "Width (CM)" },
      { dataKey: "warpPattern", title: "Pattern - Length (%)" },
      { dataKey: "weftPattern", title: "Pattern - Width (%)" },
      { dataKey: "patternNumber", title: "Pattern No" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "prodType", title: "Production Type" },
      { dataKey: "remarks", title: "Remarks" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  }
  return { columns: columns.map(e => ({ ...e, title: e?.title?.toUpperCase() })), headerRows };
};

const getSizeBreakupCells = (columns, headerRows, rows, lastRowNo, finalMaxColNo) => {
  let headerCells = [
    { rowNo: lastRowNo + 1, colNo: 1, value: "Size Breakup", cellStyle: { hAlign: "left", bold: true, border: true, bgColor: "230,230,230" } },
    { rowNo: lastRowNo + 1, colNo: 2, colEnd: finalMaxColNo === 2 ? undefined : finalMaxColNo, cellStyle: { bgColor: "230,230,230" } }
  ];
  let bgColumns = columns.map((c, idx) => ({ dataKey: c.dataKey, colNo: idx + 1 })).filter(c => c.dataKey.includes("_"));
  let sizeGroups = getDistinctValues(bgColumns.map(c => c.dataKey.split("_")[0]));
  bgColumns = bgColumns
    .map(c => ({ ...c, groupIdx: sizeGroups.findIndex(group => group === c.dataKey.split("_")[0]) }))
    .map(c => ({ ...c, bgColor: bgColors[c.groupIdx % 2] }));

  if (headerRows?.length > 1) {
    const colIdx = columns.findIndex(c => c.dataKey.includes("_"));
    headerCells.push({ rowNo: lastRowNo + 2, colNo: 1, cellStyle: { bgColor: "99,172,238", color: "255,255,255" } });
    if (colIdx === 2) {
      headerCells.push({ rowNo: lastRowNo + 2, colNo: 2, cellStyle: { bgColor: "99,172,238", color: "255,255,255" } });
    }

    const groupRow = headerRows[0];
    Object.keys(groupRow).forEach(key => {
      const value = groupRow[key];
      const colNo = columns.findIndex(c => c.dataKey === key) + 1;
      const colEnd = colNo + columns.filter(c => c.dataKey?.split("_")[0] === value)?.length - 1;
      headerCells.push({
        rowNo: lastRowNo + 2,
        colNo,
        colEnd: colNo === colEnd ? undefined : colEnd,
        value,
        cellStyle: { hAlign: "center", bgColor: "99,172,238", color: "255,255,255" }
      });
    });
    if (columns?.length < finalMaxColNo) {
      headerCells.push({
        rowNo: lastRowNo + 2,
        colNo: columns?.length,
        colEnd: columns?.length === finalMaxColNo ? undefined : finalMaxColNo,
        cellStyle: { bgColor: "99,172,238", color: "255,255,255" }
      });
    }
  }
  const start = headerRows?.length > 1 ? lastRowNo + 3 : lastRowNo + 2;
  const rowNos = headerRows?.length > 1 ? [lastRowNo + 2, lastRowNo + 3] : [lastRowNo + 2];

  const sbCellList = convertTableToCell(columns, rows, start, finalMaxColNo);
  return [...headerCells, ...sbCellList].map(cell => ({
    ...cell,
    cellStyle: {
      ...cell.cellStyle,
      bgColor: rowNos.includes(cell.rowNo) ? bgColumns.find(c => c.colNo === cell.colNo)?.bgColor || cell.cellStyle?.bgColor : cell.cellStyle?.bgColor
    }
  }));
};

const getMarkerCells = (columns, headerRows, rows, lastRowNo, finalMaxColNo) => {
  let headerCells = [
    { rowNo: lastRowNo + 1, colNo: 1, value: "Markers", cellStyle: { hAlign: "left", bold: true, border: true, bgColor: "230,230,230" } },
    { rowNo: lastRowNo + 1, colNo: 2, colEnd: finalMaxColNo === 2 ? undefined : finalMaxColNo, cellStyle: { bgColor: "230,230,230" } }
  ];

  let bgColumns = columns.map((c, idx) => ({ dataKey: c.dataKey, colNo: idx + 1 })).filter(c => c.dataKey.includes("_"));
  let sizeGroups = getDistinctValues(bgColumns.map(c => c.dataKey.split("_")[0]));
  bgColumns = bgColumns
    .map(c => ({ ...c, groupIdx: sizeGroups.findIndex(group => group === c.dataKey.split("_")[0]) }))
    .map(c => ({ ...c, bgColor: bgColors[c.groupIdx % 2] }));

  if (headerRows?.length > 1) {
    const colIdx = columns.findIndex(c => c.dataKey.includes("_"));
    headerCells.push({ rowNo: lastRowNo + 2, colNo: 1, cellStyle: { bgColor: "99,172,238", color: "255,255,255" } });
    if (colIdx === 2) {
      headerCells.push({ rowNo: lastRowNo + 2, colNo: 2, cellStyle: { bgColor: "99,172,238", color: "255,255,255" } });
    }

    const groupRow = headerRows[0];
    Object.keys(groupRow).forEach(key => {
      const value = groupRow[key];
      const colNo = columns.findIndex(c => c.dataKey === key) + 1;
      const colEnd = colNo + columns.filter(c => c.dataKey?.split("_")[0] === value)?.length - 1;
      headerCells.push({
        rowNo: lastRowNo + 2,
        colNo,
        colEnd: colNo === colEnd ? undefined : colEnd,
        value,
        cellStyle: { hAlign: "center", bgColor: "99,172,238", color: "255,255,255" }
      });
    });
    if (columns?.length < finalMaxColNo) {
      headerCells.push({
        rowNo: lastRowNo + 2,
        colNo: columns?.length,
        colEnd: columns?.length === finalMaxColNo ? undefined : finalMaxColNo,
        cellStyle: { bgColor: "99,172,238", color: "255,255,255" }
      });
    }
  }
  const start = headerRows?.length > 1 ? lastRowNo + 3 : lastRowNo + 2;
  const rowNos = headerRows?.length > 1 ? [lastRowNo + 2, lastRowNo + 3] : [lastRowNo + 2];

  const finalRows = [...rows, {}, {}, {}, {}, {}];
  const sbCellList = convertTableToCell(columns, finalRows, start, finalMaxColNo);
  return [...headerCells, ...sbCellList].map(cell => ({
    ...cell,
    cellStyle: {
      ...cell.cellStyle,
      bgColor: rowNos.includes(cell.rowNo) ? bgColumns.find(c => c.colNo === cell.colNo)?.bgColor || cell.cellStyle?.bgColor : cell.cellStyle?.bgColor
    }
  }));
};

const getMarkerExcelSheet = (
  order,
  cutOrder,
  flowInfoColumns,
  planTabIdx,
  tenant,
  i18nMessages,
  unitList = [],
  ledgerList,
  enablePerimeterUpdate = false
) => {
  //** Cut Order Info**//
  const finalFlowInfoColumns = [
    ...flowInfoColumns.filter(c => !["allocated", "expOrderQty", "status"].includes(c.dataKey)),
    { dataKey: "cutPlanRemarks", title: "Cut Plan Remarks" }
  ].map(c => ({
    ...c,
    title: lodashGet(i18nMessages, `resources.bulk.fields.${c.dataKey}`, c.title)?.toUpperCase()
  }));
  const cutOrderRows = getCutOrderRows(order, null, null, cutOrder, null, ledgerList);
  const customCutPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.CUSTOM;

  //** Size Breakup Info**//
  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
  const { columns: sbColumns, headerRows: sbHeaderRows } = getColumnsSizeBreakup(cutOrder, order);
  const sbRows = getRowsSizeBreakup(flowInfoList, order);
  const finalSbColumns = customCutPlan
    ? [{ dataKey: "", title: "" }, ...sbColumns]
    : [{ dataKey: "", title: "" }, { dataKey: "", title: "" }, ...sbColumns];

  //** Marker Info **/
  const { columns: markerColumns, headerRows: markerHeaderRows } = getMarkerColumns(order, cutOrder, enablePerimeterUpdate);
  let markerRows = getRowsMarkerDetails(order, cutOrder, planTabIdx, null, true, false);

  const finalMaxColNo = Math.max(finalFlowInfoColumns?.length, finalSbColumns?.length, markerColumns?.length);

  let cutOrderCellList = convertTableToCell(finalFlowInfoColumns, cutOrderRows, 4, finalMaxColNo);
  if (cutOrderRows?.length > 1) {
    cutOrderCellList = cutOrderCellList.map(cell => ({
      ...cell,
      cellStyle: cell.rowNo === 5 ? { ...cell.cellStyle, bgColor: "230,230,230" } : cell.cellStyle
    }));
  }
  cutOrderCellList = [
    { rowNo: 3, colNo: 1, value: "Cut Order", cellStyle: { hAlign: "left", bold: true, border: false, bgColor: "230,230,230" } },
    { rowNo: 3, colNo: 2, colEnd: finalMaxColNo === 2 ? undefined : finalMaxColNo, cellStyle: { bgColor: "230,230,230" } },
    ...cutOrderCellList
  ];

  const sbCellList = getSizeBreakupCells(
    finalSbColumns.map(e => ({ ...e, title: e?.title?.toUpperCase() })),
    sbHeaderRows,
    sbRows,
    4 + cutOrderRows?.length,
    finalMaxColNo
  );
  let lastRowNo = sbCellList.sort((a, b) => b.rowNo - a.rowNo)[0]?.rowNo;

  const markerCellList = getMarkerCells(markerColumns, markerHeaderRows, markerRows, lastRowNo, finalMaxColNo);
  const unit = unitList.find(e => e.unitId === cutOrder?.unitId) || {};

  const finalCellList = [
    {
      rowNo: 1,
      colNo: 1,
      value: unit?.displayName || unit?.name || tenant?.name || "",
      colEnd: finalMaxColNo === 1 ? undefined : finalMaxColNo,
      cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
      cellHeight: 48
    },
    {
      rowNo: 2,
      colNo: 1,
      value: "Markers Details",
      colEnd: finalMaxColNo === 1 ? undefined : finalMaxColNo,
      cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
      cellHeight: 36
    },
    ...cutOrderCellList,
    ...sbCellList,
    ...markerCellList
  ];

  const excelSheet = {
    sheetNo: 0,
    sheetName: "MARKERS",
    autoSizeColumn: false,
    excelData: { cellList: finalCellList }
  };

  return excelSheet;
};

const useStyles = makeStyles({
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5rem"
  },
  cardSelected: {
    "& > div > form > div": {
      backgroundColor: "#A7D0F6"
    },
    backgroundColor: "#A7D0F6"
  }
});

const MarkerDetails = props => {
  const classes = useStyles();
  const {
    className,
    order,
    cutOrder,
    flowInfoColumns,
    ledgerList,
    scheduledTasks = {},
    planTabIdx = 0,
    roles,
    hasAccess,
    onRefresh,
    unitList = []
  } = props;
  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  const [tabIdx, setTabIdx] = useSafeState(0);
  const [dialogState, setDialogState] = useSafeState({ active: false, name: null });
  const [importState, setImportState] = useSafeState({ importing: false, markers: null });
  const [errorList, setErrorList] = useSafeState(null);
  const [optionIdx, setOptionIdx] = useSafeState(0);
  const [fullScreen, setFullScreen] = useSafeState(false);
  const [editing, setEditing] = useSafeState(false);
  const [alertState, setAlertState] = useSafeState({ alertMsgList: [], markers: [] });

  const dispatch = useDispatch();
  const [mutate] = useMutation();
  const [download] = useMutation({ rawResponse: true });
  const tenant = useTenant();
  const i18nMessages = useSelector(state => state?.i18n?.messages || {});

  const prodTypes = useSetting(SettingKeys.PRODUCTION_TYPES);
  const enableMarkerPerimeterUpdate = useSetting(SettingKeys.ENABLE_MARKER_PERIMETER_UPDATE);
  const enablePerimeterUpdate = isTrue(enableMarkerPerimeterUpdate);
  const allowedCsvValues = useSetting(SettingKeys.ALLOWED_CSV_VALUES) || "";
  const allowedCsvList = listFromCsv(allowedCsvValues) || [];

  const { data: taskList = [] } = useQuery(
    !isEmpty(cutOrder?.id) && {
      url: "scheduledTasks",
      params: {
        search: `flowId==${cutOrder.id}`,
        version: !isEmpty(scheduledTasks) ? `${scheduledTasks[cutOrder.id]?.id}-${isEmpty(scheduledTasks[cutOrder.id]?.result)}` : 1
      }
    }
  );

  let styleColorList = cutOrder?.styleColorList || [];
  styleColorList = styleColorList.length > 1 ? [null, ...styleColorList] : styleColorList;
  const styleColor = styleColorList[tabIdx];

  const task = scheduledTasks[cutOrder?.id] || {};
  const updatingCutPlan =
    (task.name === TaskNames.CUT_PLAN_MAIN_MARKER_UPDATE || task.name === TaskNames.CUT_PLAN_REMNANT_MARKER_UPDATE) &&
    ["New", "Waiting", "Running"].includes(task.state);
  const adjustingCutPlan = task.name === TaskNames.CUT_PLAN_ADJUST && ["New", "Waiting", "Running"].includes(task.state);

  const cutPlanGeneratedTask = taskList.filter(e => e.name === TaskNames.CUT_PLAN_GENERATE).sort((a, b) => b.modifiedAt - a.modifiedAt)[0];
  const optionList =
    cutOrder?.state === OState.FINAL_GROUPING_COMPLETED.state && isTrue(cutOrder?.cutOptionsAvailable) && !isEmpty(cutPlanGeneratedTask?.result)
      ? JSON.parse(cutPlanGeneratedTask.result).sort((a, b) => b.finalScore - a.finalScore)
      : [];

  const noOfLots = flowInfoList.sort((a, b) => b.lots - a.lots)[0]?.lots || 1;
  const partCount = getDistinctValues(flowInfoList.map(e => e.part)).length;
  const hasSplicing = cutOrder?.orderConfig?.setting?.others?.includes(MiscType.SPLICING);
  const isAlgorithmicCutPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.ALGORITHMIC;

  const onSubmit = (values, form, onSubmitComplete) => {
    if (isEmpty(alertState?.alertMsgList)) {
      let alertMessageList = [];
      const bomCuList = JSON.parse(cutOrder.bomCu || "[]");
      if (!isEmpty(bomCuList)) {
        const orderingCuSum = bomCuList.reduce((acc, p) => acc + p.bomCu, 0);
        const avgOrderingCu = orderingCuSum / bomCuList.length;

        values.forEach(row => {
          if (row.efficiency && (row.efficiency < 50 || row.efficiency > 100)) {
            alertMessageList.push(`Marker - '${row.name}' efficiency should be in range 50 to 100`);
          }
          if (row.length && row.length > 1.25 * row.noOfWays * avgOrderingCu) {
            alertMessageList.push(`Marker - '${row.name}' Lay length seems too high`);
          }
          if (row.length && row.length < 0.75 * row.noOfWays * avgOrderingCu) {
            alertMessageList.push(`Marker - '${row.name}' Lay length seems too low`);
          }
        });

        if (!isEmpty(alertMessageList)) {
          setAlertState(prev => ({ ...prev, alertMsgList: alertMessageList, markers: values, onSubmitComplete }));
          return;
        }
      }
    }

    const customCutPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.CUSTOM;

    const planningDto = {
      cutOrderId: cutOrder?.id,
      poRef: order?.poRef,
      colors: cutOrder?.colors,
      styles: cutOrder?.styles,
      inseams: cutOrder?.inseams,
      destinations: cutOrder?.destinations,
      deliveries: cutOrder?.deliveries,
      flowRefs: cutOrder?.flowRefs,
      customerFlowRefs: cutOrder?.customerFlowRefs,
      productName: cutOrder?.product?.name,
      part: cutOrder?.part,
      lot: cutOrder?.lot,
      noOfLots,
      partCount,
      markers: values?.map(e => ({ ...e, length: e.markerLength })),
      manual: cutOrder?.lotType === LotType.PRE_RUN || customCutPlan
    };

    const options = {
      url: `orders/${order?.id}/markers`,
      method: "put",
      data: planningDto,
      params: { action: "default", flowId: cutOrder?.id }
    };

    const allCompleted = !hasSplicing && values.filter(e => e.plies > 1).every(el => !isEmpty(el?.efficiency) && !isEmpty(el?.length));

    mutate(options, {
      onSuccess: data => {
        onSubmitComplete(values?.map(e => ({ ...e, layLength: null, efficiency: null })));
        dispatch({ type: UPDATE_TASK, payload: { task: data } });
        if (allCompleted && !planningDto.manual) {
          allCompleted && dispatch(showNotification("Task queued. you will be notified once completed"));
        }
        setImportState({ importing: false, markers: null });
        setEditing(false);
        onRefresh && onRefresh();
      },
      onFailure: error => {
        setEditing(false);
        console.log({ error });
      }
    });
  };

  const onCancel = () => {
    alertState?.onSubmitComplete && alertState?.onSubmitComplete(alertState?.markers?.map(e => ({ ...e, length: null, efficiency: null })));
    setAlertState(prev => ({ ...prev, alertMsgList: [], markers: [] }));
  };

  const onAlertSubmit = () => {
    onSubmit && onSubmit(alertState?.markers, null, alertState?.onSubmitComplete);
    setAlertState(prev => ({ ...prev, alertMsgList: [], markers: [] }));
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "import") {
      if (importState.importing) {
        setImportState({ importing: false });
      }
      setDialogState({ active: true, name: DialogNames.IMPORT });
    } else if (action === "splice") {
      setDialogState({ active: true, name: DialogNames.SPLICE_POINTS });
    } else if (action === "export") {
      const filename = `Markers-${order?.poRef?.replaceAll("/", "-")}.xlsx`;

      const excelSheet = getMarkerExcelSheet(
        order,
        cutOrder,
        flowInfoColumns,
        planTabIdx,
        tenant,
        i18nMessages,
        unitList,
        ledgerList,
        enablePerimeterUpdate
      );

      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [excelSheet]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fName = header ? header.match(/filename="(.+)"/)[1] : filename;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fName;
            link.click();
          }
        }
      });
    } else if (action === "delete") {
      deleteMarkers();
    } else if (action === "adjust") {
      // if (cutOrder?.orderConfig?.setting?.cutPlanType !== CutPlanType.CUSTOM) {
      //   adjustCutPlan();
      // } else {
      //   setDialogState({ active: true, name: DialogNames.ADJUST });
      // }
      setDialogState({ active: true, name: DialogNames.ADJUST });
    } else if (action === "update") {
      setDialogState({ active: true, name: DialogNames.MANUAL_MARKER });
    } else if (action === "template") {
      const filename = `Markers-${order?.poRef?.replaceAll("/", "-")}.xlsx`;

      const excelSheet = getMarkerExcelSheet(
        order,
        cutOrder,
        flowInfoColumns,
        planTabIdx,
        tenant,
        i18nMessages,
        unitList,
        ledgerList,
        enablePerimeterUpdate
      );

      let sheetList = [excelSheet];
      if (cutOrder?.lotType === LotType.PRE_RUN) {
        const columnList = [
          { dataKey: "prodType", title: "PRODUCTION TYPE" },
          { dataKey: "csv", title: "CSV" }
        ];

        const productionTypes = listFromCsv(prodTypes);
        const rowCount = productionTypes?.length > allowedCsvList?.length ? productionTypes?.length : allowedCsvList?.length;
        let rows = [];
        for (let i = 0; i < rowCount; i++) {
          rows.push({ prodType: productionTypes[i], csv: allowedCsvList[i] });
        }

        const rowList = rows.map((row, idx) => ({ rowNo: idx + 1, cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] })) }));

        sheetList.push({
          sheetNo: 1,
          sheetName: "LIST OF VALUES",
          autoSizeColumn: true,
          excelData: { columnList, rowList }
        });
      }

      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: sheetList
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            const fName = header ? header.match(/filename="(.+)"/)[1] : filename;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fName;
            link.click();
          }
        }
      });
    } else if (action === "upload") {
      setDialogState({ active: true, name: DialogNames.UPLOAD, onActionComplete });
    }
    setEditing(action === "edit");
    if (action === "cancel") {
      setImportState({ importing: false });
    }
    onActionComplete && onActionComplete(rows);
  };

  const onMarkerImport = async values => {
    const data = new FormData();
    data.append("file", values.file?.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];

          const sizeBreakupList = findSizeBreakupList(cutOrder, order);
          const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup)).length > 1;

          const markerRowNo = excelRowList.find(r => r.cellList?.some(c => `${c.value}`.trim().toUpperCase() === "MARKERS"))?.rowNo;
          const rowStartNo = haveSizeGroup ? markerRowNo + 2 : markerRowNo + 1;
          const tableRowList = excelRowList.filter(r => r.rowNo >= rowStartNo).filter(r => !isEmpty(r.cellList));
          const sizeGroupRow = haveSizeGroup ? excelRowList.find(e => e.rowNo === markerRowNo + 1) : {};
          const headerRow = tableRowList[0];
          const headerMap = headerRow.cellList.reduce(
            (acc, c) => ({
              ...acc,
              [`${c.colNo}`]:
                !isEmpty(c.value) && `${c.value}`.trim().endsWith("*")
                  ? `${c.value}`.trim().substring(0, `${c.value}`.trim().length - 1)
                  : c.value || ""
            }),
            {}
          );
          const sizeGroupMap = isEmpty(sizeGroupRow?.cellList)
            ? {}
            : sizeGroupRow.cellList
                .filter(cell => !isEmpty(cell.value))
                .flatMap(cell =>
                  Array(cell.colEnd - cell.colNo + 1)
                    .fill("")
                    .map((_, idx) => ({ colNo: cell.colNo + idx, value: cell.value }))
                )
                .reduce((acc, e) => ({ ...acc, [e.colNo]: e.value }), {});

          const markerList = tableRowList
            .slice(1)
            .map(r => {
              const sizeRatioList = r.cellList
                .map((cell, idx) => ({
                  serialNo: idx,
                  sizeKey: `${!isEmpty(sizeGroupMap[cell.colNo]) ? `${sizeGroupMap[cell.colNo]}_` : ""}${headerMap[cell.colNo]}`,
                  size: headerMap[cell.colNo],
                  sizeGroup: sizeGroupMap[cell.colNo],
                  ratio: cell.value
                }))
                .filter(e => !isEmpty(e.ratio))
                .map(e => ({
                  sizeRatio: e,
                  sizeBreakup: findMatchingSizeBreakup(sizeBreakupList, e.sizeKey, e.size, e.sizeGroup)
                }))
                .filter(e => !isEmpty(e.sizeBreakup))
                .map(({ sizeRatio, sizeBreakup }) => ({
                  ...sizeRatio,
                  size: sizeBreakup.size,
                  sizeGroup: sizeBreakup.sizeGroup,
                  sizeKey: sizeBreakup.sizeKey
                }));
              return { ...r, sizeRatioList };
            })
            .map(r =>
              r.cellList.reduce((acc, c) => ({ ...acc, [headerMap[`${c.colNo}`].toUpperCase()]: c.value }), { sizeRatioList: r.sizeRatioList || [] })
            )
            .map(m => {
              const noOfWays = m.sizeRatioList.reduce((acc, sr) => acc + Number(sr.ratio), 0);
              const name = m.sizeRatioList.map(e => `${e.sizeKey}:${e.ratio}`).join(", ");
              const style = m["STYLE"] || cutOrder.styles;
              const color = m["COLOR"] || cutOrder.colors;
              const plies = m["PLIES"];
              const markerPlyList = [{ style, color, styleColor: getStyleColor(style, color), plannedPlies: plies }];
              const patternValue = m["PATTERN VALUE"];
              return {
                id: m["ID"],
                markerNo: m["M. NO."],
                sizeGroup: !isEmpty(m.sizeRatioList) ? m.sizeRatioList[0]?.sizeGroup : null,
                name,
                noOfWays,
                width: m["WIDTH (CM)"],
                patternValue,
                patternNumber: m["PATTERN NO"],
                ...(isEmpty(patternValue) ? {} : parseMarkerPattern(patternValue)),
                csv: m["CSV"],
                length: m["LENGTH"],
                efficiency: m["EFFICIENCY"],
                perimeter: m["PERIMETER"],
                sizeRatioList: m.sizeRatioList,
                markerPlyList
              };
            })
            .filter(m => !isEmpty(m.name) && m.noOfWays > 0)
            .map(marker => {
              let eMarker = cutOrder?.markerList?.find(m => m.id === marker.id);
              if (isEmpty(eMarker)) {
                eMarker = cutOrder?.markerList?.find(
                  m =>
                    isSameMarkerIdentity(m, marker) &&
                    marker.name === m.name &&
                    marker.width === m.group?.widthAxis &&
                    isEqual(marker.csv, m.group?.csv)
                );
              }
              return { ...marker, id: eMarker?.id };
            });

          setImportState({ importing: true, markers: markerList });
        }
        setDialogState({ active: false, name: null });
      },
      onFailure: err => {
        setDialogState({ active: false, name: null });
      }
    });
  };

  const onMarkerUpload = values => {
    const data = new FormData();
    data.append("file", values.file?.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];

          const sizeBreakupList = findSizeBreakupList(cutOrder, order);
          const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup)).length > 1;

          const markerRowNo = excelRowList.find(r => r.cellList?.some(c => `${c.value}`.trim().toUpperCase() === "MARKERS"))?.rowNo;
          const rowStartNo = haveSizeGroup ? markerRowNo + 2 : markerRowNo + 1;
          const tableRowList = excelRowList.filter(r => r.rowNo >= rowStartNo).filter(r => !isEmpty(r.cellList));
          const sizeGroupRow = haveSizeGroup ? excelRowList.find(e => e.rowNo === markerRowNo + 1) : {};
          const headerRow = tableRowList[0];
          const headerMap = headerRow.cellList.reduce(
            (acc, c) => ({
              ...acc,
              [`${c.colNo}`]:
                !isEmpty(c.value) && `${c.value}`.trim().endsWith("*")
                  ? `${c.value}`.trim().substring(0, `${c.value}`.trim().length - 1)
                  : c.value || ""
            }),
            {}
          );
          const sizeGroupMap = isEmpty(sizeGroupRow?.cellList)
            ? {}
            : sizeGroupRow.cellList
                .filter(cell => !isEmpty(cell.value))
                .flatMap(cell =>
                  Array((cell.colEnd || cell.colNo) - cell.colNo + 1)
                    .fill("")
                    .map((_, idx) => ({ colNo: cell.colNo + idx, value: cell.value }))
                )
                .reduce((acc, e) => ({ ...acc, [e.colNo]: e.value }), {});

          const markerList = tableRowList
            .slice(1)
            .map(r => {
              const sizeRatioList = r.cellList
                .map((cell, idx) => ({
                  serialNo: idx,
                  sizeKey: `${!isEmpty(sizeGroupMap[cell.colNo]) ? `${sizeGroupMap[cell.colNo]}_` : ""}${headerMap[cell.colNo]}`,
                  size: headerMap[cell.colNo],
                  sizeGroup: sizeGroupMap[cell.colNo],
                  ratio: cell.value
                }))
                .filter(e => !isEmpty(e.ratio))
                .map(e => ({
                  sizeRatio: e,
                  sizeBreakup: findMatchingSizeBreakup(sizeBreakupList, e.sizeKey, e.size, e.sizeGroup)
                }))
                .filter(e => !isEmpty(e.sizeBreakup))
                .map(({ sizeRatio, sizeBreakup }) => ({
                  ...sizeRatio,
                  size: sizeBreakup.size,
                  sizeGroup: sizeBreakup.sizeGroup,
                  sizeKey: sizeBreakup.sizeKey
                }));
              return { ...r, sizeRatioList };
            })
            .map(r =>
              r.cellList.reduce((acc, c) => ({ ...acc, [headerMap[`${c.colNo}`].toUpperCase()]: c.value }), { sizeRatioList: r.sizeRatioList || [] })
            )
            .map(m => {
              const noOfWays = m.sizeRatioList.reduce((acc, sr) => acc + Number(sr.ratio), 0);
              const name = m.sizeRatioList
                .filter(e => Number(e.ratio) > 0)
                .map(e => `${e.sizeKey}:${e.ratio}`)
                .join(", ");
              const style = m["STYLE"] || cutOrder.styles;
              const color = m["COLOR"] || cutOrder.colors;
              const plies = m["PLIES"];
              const markerPlyList = [{ style, color, styleColor: getStyleColor(style, color), plannedPlies: plies }];
              const patternValue = m["PATTERN VALUE"];
              return {
                id: m["ID"],
                markerNo: m["M. NO."],
                markerType: MarkerType.MANUAL,
                sizeGroup: !isEmpty(m.sizeRatioList) ? m.sizeRatioList[0]?.sizeGroup : null,
                name,
                noOfWays,
                width: m["WIDTH (CM)"],
                warpPattern: m["PATTERN - LENGTH (%)"],
                weftPattern: m["PATTERN - WIDTH (%)"],
                patternValue,
                patternNumber: m["PATTERN NO"],
                shade: m["SHADE"],
                csv: m["CSV"],
                prodType: m["PRODUCTION TYPE"],
                remarks: m["REMARKS"],
                qtyPercent: "",
                length: m["LENGTH"],
                efficiency: m["EFFICIENCY"],
                perimeter: m["PERIMETER"],
                sizeRatioList: m.sizeRatioList,
                markerPlyList,
                plies,
                ...(isEmpty(patternValue) ? {} : parseMarkerPattern(patternValue))
              };
            })
            .filter(m => !isEmpty(m.name) && m.noOfWays > 0)
            .map(marker => {
              const eMarker = !isEmpty(marker.id)
                ? cutOrder?.markerList?.find(m => `${m.id}` === `${marker.id}`)
                : cutOrder?.markerList?.find(
                    m =>
                      isSameMarkerIdentity(m, marker) &&
                      m.name === marker.name &&
                      m.width === marker.width &&
                      isEqual(m.csv, marker.csv) &&
                      isEqual(m.prodType, marker.prodType) &&
                      isEqual(m.shade, marker.shade)
                  );
              const mMarkerPlyList = eMarker?.markerPlyList || [];
              return {
                ...marker,
                id: eMarker?.id,
                markerPlyList: marker.markerPlyList.map(mp => ({
                  ...mp,
                  id: mMarkerPlyList.find(e => e.style === mp.style && e.color === mp.color)?.id
                }))
              };
            });

          const productionTypes = listFromCsv(prodTypes);

          // const bomCuList = JSON.parse(cutOrder.bomCu || "[]");
          // let avgOrderingCu = 0;
          // if (!isEmpty(bomCuList)) {
          //   const orderingCuSum = bomCuList.reduce((acc, p) => acc + p.bomCu, 0);
          //   avgOrderingCu = orderingCuSum / bomCuList.length;
          // }

          const errorList = markerList.flatMap((marker, idx) => {
            let errList = requiredCols
              .filter(dataKey => isEmpty(marker[dataKey]))
              .map(dataKey => ({ marker: `M${idx + 1}`, field: dataKey, message: "Required" }));
            if (!isEmpty(marker.csv) && !allowedCsvList.includes(marker.csv)) {
              errList.push({
                marker: `M${idx + 1}`,
                field: "CSV",
                message: `Invalid CSV Value. CSV value must be one of: [${allowedCsvList.join(", ")}]`
              });
            }
            if (!isEmpty(marker.prodType) && !productionTypes.includes(marker.prodType)) {
              errList.push({
                marker: `M${idx + 1}`,
                field: "PRODUCTION TYPE",
                message: `Invalid Production Type. Production Type must be one of: [${productionTypes.join(", ")}]`
              });
            }

            if (marker.efficiency && (marker.efficiency < 0 || marker.efficiency > 100)) {
              errList.push({
                marker: `M${idx + 1}`,
                field: "EFFICIENCY",
                message: `Efficiency should be in range 0 to 100`
              });
            }
            // if (avgOrderingCu > 0) {
            //   if (marker.length && marker.length > 1.25 * marker.noOfWays * avgOrderingCu) {
            //     errList.push({
            //       marker: `M${idx + 1}`,
            //       field: "LAY LENGTH",
            //       message: `Lay length seems too high`
            //     });
            //   }
            //   if (marker.length && marker.length < 0.75 * marker.noOfWays * avgOrderingCu) {
            //     errList.push({
            //       marker: `M${idx + 1}`,
            //       field: "LAY LENGTH",
            //       message: `Lay length seems too low`
            //     });
            //   }
            // }

            if (marker?.warpPattern === "null" || marker?.weftPattern === "null") {
              errList.push({
                marker: `M${idx + 1}`,
                field: "Pattern Value",
                message: "Invalid format for Pattern Value. Valid example - [(L3.5%, W1.5%) or (3.5, 1.5) or (3.5%, 1.5%)]"
              });
            }

            return errList;
          });

          if (!isEmpty(errorList)) {
            setErrorList(errorList);
            return;
          }

          const options = {
            url: `orders/${order?.id}/markers`,
            method: "put",
            data: { markers: markerList, manual: true },
            params: { flowId: cutOrder?.id, action: "default" }
          };

          mutate(options, {
            onSuccess: data => {
              dispatch(showNotification("Markers updated successfully."));
              setDialogState({ active: false, name: null });
              onRefresh && onRefresh();
            }
          });
        }
        // setDialogState({ active: false, name: null });
      },
      onFailure: err => {
        setDialogState({ active: false, name: null });
      }
    });
  };

  const adjustCutPlan = (values, onActionComplete) => {
    // const hasProductRatio = flowInfoList.filter(fi => !isEmpty(fi.productRatio)).length > 1;
    // const productIds = hasProductRatio ? flowInfoList.filter(fi => !isEmpty(fi.productRatio)).map(fi => fi.product?.id) : [orderFlow.product?.id];
    // const flowList = order.orderFlowList
    //   ?.filter(of => of.styles === orderFlow?.styles && of.colors === orderFlow?.colors && productIds.includes(of.product?.id))
    //   ?.filter(of => of.stateValue > OState.NEW.stateValue);
    // const flowIds = isEmpty(flowList) ? [orderFlow?.id] : flowList.map(e => e.id);
    const noOfLots = flowInfoList.sort((a, b) => b.lots - a.lots)[0]?.lots || 1;
    const partCount = getDistinctValues(flowInfoList.map(e => e.part)).length;

    const planningDto = {
      // flowIds: flowIds.join(","),
      cutOrderId: cutOrder?.id,
      poRef: order?.poRef,
      colors: cutOrder?.colors,
      styles: cutOrder?.styles,
      inseams: cutOrder?.inseams,
      destinations: cutOrder?.destinations,
      deliveries: cutOrder?.deliveries,
      flowRefs: cutOrder?.flowRefs,
      customerFlowRefs: cutOrder?.customerFlowRefs,
      productName: cutOrder?.product?.name,
      part: cutOrder?.part,
      lot: cutOrder?.lot,
      noOfLots,
      partCount,
      extraCutPercentList: values
    };

    const options = {
      url: `orders/${order?.id}/markers`,
      method: "put",
      params: {
        action: "adjust_cut_plan",
        flowId: cutOrder?.id
      },
      data: planningDto
    };
    mutate(options, {
      onSuccess: data => {
        dispatch({ type: UPDATE_TASK, payload: { task: data } });
        dispatch(showNotification("Task queued. you will be notified once completed."));
        onActionComplete && onActionComplete(values);
        setDialogState({ active: false, name: null });
      }
    });
  };

  const saveMarkers = () => {
    const customCutPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.CUSTOM;

    const option = optionList[optionIdx];
    const options = {
      url: `orders/${order?.id}/markers`,
      method: "post",
      data: { markers: option?.markerList || [], cutSetting: option?.setting, manual: !customCutPlan },
      params: { flowId: cutOrder?.id, action: "default" }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Markers updated successfully."));
        setFullScreen(false);
        onRefresh && onRefresh();
      }
    });
  };

  const saveManualMarkers = (markers, onActionComplete) => {
    const options = {
      url: `orders/${order?.id}/markers`,
      method: "put",
      data: { markers, manual: true },
      params: { flowId: cutOrder?.id, action: "default" }
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Markers updated successfully."));
        onActionComplete && onActionComplete([]);
        setDialogState({ active: false, name: null });
        onRefresh && onRefresh();
      }
    });
  };

  const deleteMarkers = () => {
    const options = {
      url: `orders/${order?.id}/markers`,
      method: "delete",
      params: { flowId: cutOrder?.id, stage: planTabIdx }
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Cut Plan deleted successfully."));
        onRefresh && onRefresh();
      }
    });
  };

  const onClose = () => {
    setDialogState({ active: false, name: null });
  };

  //** Prepare Columns for Marker Details **//
  const { columns, headerRows } = useDeepMemo(
    () => getColumnsMarkerDetails(order, cutOrder, enablePerimeterUpdate),
    [order, cutOrder, enableMarkerPerimeterUpdate]
  );

  //** Prepare Rows for Marker Details**//
  const rows = useDeepMemo(() => {
    let rowList = getRowsMarkerDetails(order, cutOrder, planTabIdx, styleColor);
    if (importState.importing) {
      const markers = importState.markers || [];
      rowList = rowList.map(m => {
        const marker = markers.find(row => row.id === m.id) || {};
        return {
          ...m,
          length: marker.length,
          markerLength: marker.length,
          perimeter: marker.perimeter,
          layLength: isTrue(m.completed) ? m.layLength : marker.length,
          efficiency: isTrue(m.completed) ? m.efficiency : marker.efficiency
        };
      });
    }
    return rowList;
  }, [order, cutOrder, planTabIdx, styleColor, importState]);

  const customCutPlan = cutOrder?.orderConfig?.setting?.cutPlanType === CutPlanType.CUSTOM;
  const isSplicePointsAvailable = cutOrder?.markerList?.every(m => !isEmpty(m.splicePoints));
  const isPreRun = cutOrder?.lotType === LotType.PRE_RUN;
  let editable =
    cutOrder?.stateValue === OState.MAIN_CUTPLAN_GENERATED.stateValue ||
    cutOrder?.stateValue === OState.MAIN_CUTPLAN_COMPLETED.stateValue ||
    cutOrder?.stateValue === OState.CUTPLAN_COMPLETED.stateValue ||
    cutOrder?.stateValue === OState.CUTPLAN_IN_PROGRESS.stateValue ||
    cutOrder?.stateValue === OState.RE_PLANNING_IN_PROGRESS.stateValue;
  const cutPlanSentOrApproved = cutOrder?.orderConfig?.cutPlanApproval === "Sent" || cutOrder?.orderConfig?.cutPlanApproval === "Approved";
  const isAdmin = roles?.includes(Role.ADMIN_USER) || roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER);
  if (cutOrder?.reGenerated && planTabIdx === 0) {
    // Freeze First Plan on RePlan
    editable = false;
  }

  let footerActions = [];
  if (!customCutPlan) {
    footerActions = [
      {
        name: "delete",
        tooltip: "Delete",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "export",
        tooltip: "Export",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      // {
      //   name: "upload",
      //   tooltip: "Upload",
      //   options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      // },
      {
        name: "import",
        tooltip: "Import",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "adjust",
        tooltip: "Adjust",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "splice",
        tooltip: "Splice Points",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "edit",
        tooltip: "Update",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      }
    ];

    if (!isEmpty(cutOrder?.orderConfig?.layType) || cutOrder?.reGenerated || (cutPlanSentOrApproved && !isAdmin)) {
      footerActions = footerActions.filter(e => e.name !== "delete");
    }
    if (
      !isEmpty(cutOrder?.orderConfig?.layType) ||
      cutPlanSentOrApproved ||
      cutOrder?.reGenerated ||
      cutOrder?.stateValue <= OState.MAIN_CUTPLAN_GENERATED.stateValue
    ) {
      footerActions = footerActions.filter(e => !["adjust"].includes(e.name));
    }
    // if (cutOrder?.orderConfig?.setting.cutPlanType !== CutPlanType.CUSTOM) {
    //   const leftPlies = cutOrder?.markerList
    //     ?.flatMap(m => m.markerPlyList || [])
    //     .map(mp => mp.shellPlies - mp.plannedPlies)
    //     .reduce((acc, e) => acc + e, 0);
    //   if (!(cutOrder?.stateValue === OState.CUTPLAN_COMPLETED.stateValue && leftPlies > 0)) {
    //     footerActions = footerActions.filter(e => e.name !== "adjust");
    //   }
    // }
    if (!editable) {
      footerActions = footerActions.filter(e => e.name !== "import");
    }
    if (updatingCutPlan || adjustingCutPlan) {
      footerActions = footerActions.map(e => ({
        ...e,
        options: { ...e.options, disabled: true },
        icon:
          (updatingCutPlan && e.name === "edit") || (adjustingCutPlan && e.name === "adjust") ? (
            <CircularProgress size={16} thickness={2} color="secondary" />
          ) : null
      }));
    }

    if (!hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.UPDATE)) {
      footerActions = footerActions.filter(e => e.name !== "import");
      footerActions = footerActions.filter(e => e.name !== "adjust");
    }
    if (!hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.DELETE)) {
      footerActions = footerActions.filter(e => e.name !== "delete");
    }
    if (!hasSplicing) {
      footerActions = footerActions.filter(e => e.name !== "splice");
    }
  } else {
    footerActions = [
      {
        name: "delete",
        tooltip: "Delete",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "template",
        tooltip: "Template",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "upload",
        tooltip: "Upload",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      // {
      //   name: "update",
      //   tooltip: "Update",
      //   options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      // },
      {
        name: "splice",
        tooltip: "Splice Points",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      },
      {
        name: "edit",
        tooltip: "Update",
        options: { variant: "outlined", color: "primary", style: { margin: "0.5em" } }
      }
    ];
    if (isEmpty(cutOrder?.markerList) || !hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.DELETE)) {
      footerActions = footerActions.filter(e => e.name !== "delete");
    }
    if (!hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.UPDATE)) {
      footerActions = footerActions.filter(e => e.name !== "upload");
    }
    if (!hasSplicing) {
      footerActions = footerActions.filter(e => e.name !== "splice");
    }
    if (cutOrder?.stateValue === OState.CLOSED.stateValue) {
      footerActions = [];
    }
  }

  // if (
  //   orderFlow?.stateValue !== OState.RE_PLANNED_REMNANT_CUTPLAN_IN_PROGRESS.stateValue &&
  //   orderFlow?.stateValue > OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue
  // ) {
  //   footerActions = [];
  // }
  let visible = true;
  if (isPreRun && cutOrder?.stateValue < OState.FABRIC_ASSOCIATED.stateValue) {
    visible = false;
  }
  if (!isPreRun && customCutPlan && cutOrder?.stateValue < OState.FINAL_GROUPING_COMPLETED.stateValue) {
    visible = false;
  }
  if (
    !customCutPlan &&
    (cutOrder?.stateValue < OState.FINAL_GROUPING_COMPLETED.stateValue ||
      (cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue && isFalse(cutOrder?.cutOptionsAvailable)))
  ) {
    visible = false;
  }

  if (!visible) {
    return <div />;
  }
  const hasUpdateAccess = hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.UPDATE);

  const enableEdit = tabIdx === 0 && editable && hasUpdateAccess && !isEmpty(rows);
  const busy = false;

  return (
    <div>
      {alertState?.alertMsgList && alertState?.alertMsgList?.length > 0 && (
        <SweetAlert warning={true} title="Warning" onConfirm={onAlertSubmit} onCancel={onCancel} focusCancelBtn showCancel confirmBtnText={"Proceed"}>
          <div style={{ textAlign: "left" }}>
            {alertState?.alertMsgList?.map((msg, idx) => (
              <Typography key={idx}>{msg}</Typography>
            ))}
          </div>
        </SweetAlert>
      )}

      {/** Dialogs */}
      <CutPlanAdjustDialog
        open={dialogState.active && dialogState.name === DialogNames.ADJUST}
        orderFlow={cutOrder}
        flowInfoList={flowInfoList}
        onSubmit={adjustCutPlan}
        onClose={onClose}
      />

      <MarkerImportDialog open={dialogState.active && dialogState.name === DialogNames.IMPORT} onSubmit={onMarkerImport} onClose={onClose} />

      <MarkerUploadDialog open={dialogState.active && dialogState.name === DialogNames.UPLOAD} onSubmit={onMarkerUpload} onClose={onClose} />

      <ManualMarkerDialog
        open={dialogState.active && dialogState.name === DialogNames.MANUAL_MARKER}
        order={order}
        orderFlow={cutOrder}
        onSubmit={saveManualMarkers}
        onClose={onClose}
      />

      <ExcelErrorDialog
        maxWidth="xl"
        open={errorList?.length > 0 || false}
        columns={[
          { dataKey: "marker", title: "Marker" },
          { dataKey: "field", title: `Field` },
          { dataKey: "message", title: "Message" }
        ]}
        errors={errorList}
        onClose={() => setErrorList(null)}
      />

      <SplicePointDrawer
        open={dialogState.active && dialogState.name === DialogNames.SPLICE_POINTS}
        order={order}
        cutOrder={cutOrder}
        noOfLots={noOfLots}
        partCount={partCount}
        onRefresh={onRefresh}
        onDrawerClose={onClose}
      />

      <FullScreen enabled={fullScreen} onChange={value => setFullScreen(value)}>
        <Card className={fullScreen ? null : className}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <CardHeader title="Cut Plan" />
              {hasSplicing && !isSplicePointsAvailable && (
                <Typography style={{ marginLeft: "1em", color: "red" }}>
                  <ErrorIcon style={{ verticalAlign: "middle", marginRight: "0.5em" }} />
                  {isAlgorithmicCutPlan
                    ? "Splice points are not available for all markers. Please update splice points to run roll plan."
                    : "Splice points are not available for all markers. Please update splice points to complete cut plan."}
                </Typography>
              )}
            </div>

            <Button
              style={{ marginRight: "0.5em", width: 50 }}
              showLabel={false}
              label="resources.dashboard.button.full_screen"
              onClick={() => setFullScreen(prev => !prev)}>
              {!fullScreen && <FullscreenIcon />}
              {fullScreen && <FullscreenExitIcon />}
            </Button>
          </div>

          <Divider />
          {styleColorList?.length > 1 && (
            <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)} variant="scrollable" scrollButtons="auto">
              {styleColorList.map((styleColor, index) => (
                <Tab key={index} label={isEmpty(styleColor) ? "Combined" : styleColor} />
              ))}
            </Tabs>
          )}
          {styleColorList?.length > 1 && <Divider />}
          {optionList?.length > 1 && (
            <PerfectScrollbar style={{ padding: "1em", maxHeight: "89vh" }}>
              {optionList.map((option, idx) => {
                return (
                  <Card
                    key={idx}
                    style={{ margin: "0.75em 0" }}
                    className={clsx({ [classes.cardSelected]: optionIdx === idx })}
                    onClick={() => setOptionIdx(idx)}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <CardHeader title={`Option ${idx + 1}`} />
                      <div style={{ display: "flex", alignItems: "center", marginRight: "1em" }}>
                        <div style={{ margin: "0 0.5em" }}>Size Exhausted = {option.sizesExhausted}</div>
                        <div style={{ margin: "0 0.5em" }}>Size Mixing = {toFixed(option.weightedSizeMixing)}</div>
                        <div style={{ margin: "0 0.5em" }}>Balance Qty = {toFixed(option.balanceQtyScore)}</div>
                        <div style={{ margin: "0 0.5em" }}>Final Score = {toFixed(option.finalScore)}</div>
                        <Tooltip
                          title={`SE=${option.setting?.sizeExhaustConstant}, WM=${option.setting?.wayMaximizeConstant}, SM=${option.setting?.sizeMixingConstant}, HS=${option.setting?.preferHigherSizeConstant}`}>
                          <span>...</span>
                        </Tooltip>
                      </div>
                    </div>

                    <Divider style={{ backgroundColor: "rgba(240, 240, 240, 1)" }} />
                    <MuiTable
                      // cellStyle={({ row }) => ({
                      //   color: row.markerNo === "Planned" || row.markerNo === "Balance" ? "#405161" : "none"
                      // })}
                      cellStyle={({ row }) => ({
                        color: row.name === "Total" || row.name === "Planned" || row.name === "Balance" ? "#405161" : "none"
                      })}
                      columns={columns}
                      rows={getRowsMarkerDetails(
                        order,
                        {
                          ...cutOrder,
                          markerList: option?.markerList || [],
                          orderConfig: {
                            allowedQty: !isEmpty(option?.allowedQtyMap) ? JSON.stringify(option.allowedQtyMap) : null
                          }
                        },
                        0,
                        styleColor
                      )}
                      fontSize={16}
                      cellLength={50}
                      cellOverFlow="wrap"
                    />
                  </Card>
                );
              })}
            </PerfectScrollbar>
          )}
          {(isEmpty(optionList) || optionList.length <= 1) && (
            <PerfectScrollbar>
              <MuiTable
                headerCellStyle={{ backgroundColor: "#C6D0DA" }}
                cellStyle={({ row }) => ({
                  color: row.name === "Total" || row.name === "Planned" || row.name === "Balance" ? "#405161" : "none"
                })}
                key={`${JSON.stringify(rows)}-${importState.importing}-${editing}-${JSON.stringify(footerActions.map(e => ({ ...e, icon: null })))}`}
                columns={columns}
                headerRows={headerRows}
                rows={rows}
                // editable={tabIdx === 0 && editable && !manualCutPlan && hasUpdateAccess}
                editable={enableEdit}
                editing={importState.importing || editing || false}
                footerActions={tabIdx === 0 && !isEmpty(footerActions) ? footerActions : undefined}
                onSubmit={onSubmit}
                onFooterActionClick={onFooterActionClick}
                fontSize={16}
                cellLength={50}
                cellOverFlow="wrap"
                emptyMessage="Markers not available!"
              />
            </PerfectScrollbar>
          )}
          {optionList?.length > 1 && <Divider />}
          {optionList?.length > 1 && (
            <div className={classes.footer}>
              <MuiButton color="primary" variant="outlined" disabled={busy} onClick={deleteMarkers}>
                <ActionCancel className={classes.icon} />
                CANCEL
              </MuiButton>
              <MuiButton style={{ marginLeft: "1.5em" }} color="primary" variant="contained" disabled={busy} onClick={saveMarkers}>
                {busy ? (
                  <CircularProgress size={20} thickness={2} color="secondary" className={classes.icon} />
                ) : (
                  <ActionSave className={classes.icon} />
                )}
                SAVE
              </MuiButton>
            </div>
          )}
        </Card>
      </FullScreen>
    </div>
  );
};

export default MarkerDetails;
